import { Observable, Subject } from 'rxjs';
import { AiService } from 'src/app/shared/services/ai.service';
import * as cheerio from 'cheerio';

export type AIStatusType = 'none' | 'loading' | 'done' | 'finalizing' | 'error';

export abstract class BaseBlockEditor {
  public aiStatus: AIStatusType = 'none';
  public aiGeneratedContent = '';
  public isInitialized = false;
  public isEditorReady = false;
  public isInitialValueSet = false;
  public content = '';
  public lastEmitContent = '';

  private changeSubject = new Subject<string>();
  private readySubject = new Subject<void>();
  public onChange$: Observable<string> = this.changeSubject.asObservable();
  public onReady$: Observable<void> = this.readySubject.asObservable();

  public aiTimerConfig: any = {};
  public promptFunction?: (...args: any[]) => string;

  constructor(
    promptFunction?: (...args: any[]) => string,
    protected aiService?: AiService,
  ) {
    this.promptFunction = promptFunction;
  }

  protected emitChange(content: string) {
    this.lastEmitContent = content;
    this.changeSubject.next(content);
  }

  protected emitReady() {
    this.readySubject.next();
  }

  public setAiStatus(status: AIStatusType) {
    this.aiStatus = status;
    if (status === 'done') {
      setTimeout(() => {
        this.scrollToEditorEnd();
        setTimeout(() => this.setAiStatus('none'), 2000);
      }, 100);
    }
  }

  public resetAiState() {
    this.aiGeneratedContent = '';
  }

  public startTimer() {
    this.aiTimerConfig.startTime = Date.now();
    this.aiTimerConfig.timerId = setInterval(() => {
      this.aiTimerConfig.elapsedTime =
        Date.now() - this.aiTimerConfig.startTime;
    }, 100);
  }

  public clearTimer() {
    this.stopTimer();
    this.aiTimerConfig.elapsedTime = null;
  }

  public stopTimer() {
    clearInterval(this.aiTimerConfig.timerId);
  }

  public scrollToEditorEnd() {
    const el = document.getElementById(this.getEditorID());
    el?.scrollBy({ top: 9999, behavior: 'smooth' });
  }

  public optimizeHTMLContent = (content: string) => {
    if (!content) {
      return '';
    }
    const $ = cheerio.load(content);

    $('img, iframe').remove();

    const body = $('body');

    body.contents().each((index, element) => {
      const elementHTML = $(element);
      const text = elementHTML.text().trim();

      let matchedPattern: string | null | RegExp = null;
      let listType = 'ul';

      if (text.includes('- ')) {
        matchedPattern = '- ';
        listType = 'ul';
      } else if (text.includes('* ')) {
        matchedPattern = '* ';
        listType = 'ul';
      } else if (text.includes('* ')) {
        matchedPattern = '• ';
        listType = 'ul';
      } else if (text.match(/^\d+\.\s/gm)) {
        matchedPattern = /^\d+\.\s/gm;
        listType = 'ol';
      }

      if (matchedPattern) {
        const items = text
          .split(matchedPattern)
          .map((item) => item.trim())
          .filter((item) => item.length > 0);

        const firstItemHasHyphen = items[0].startsWith(
          matchedPattern.toString(),
        );
        const firstTitle = firstItemHasHyphen ? '' : items[0];
        const filteredItems = firstItemHasHyphen ? items : items.slice(1);

        if (filteredItems.length < 2) {
          return;
        }

        console.log(filteredItems);
        const listItems = filteredItems
          .map((item) => `<li>${item}</li>`)
          .join('');
        const listElement = `<${listType}>${listItems}</${listType}>`;

        elementHTML.replaceWith(`<div>${firstTitle}${listElement}</div>`);
      }
    });

    $('li').each((index, element) => {
      const $li = $(element);
      const parentTag = $li.parent().prop('tagName').toLowerCase();

      // Ensure <li> is inside a valid list
      if (parentTag !== 'ul' && parentTag !== 'ol') {
        const $ul = $('<ul></ul>');

        // Move current <li> and any immediately following <li> elements
        let $next = $li;
        while ($next.length && $next.prop('tagName').toLowerCase() === 'li') {
          const $move = $next;
          $next = $next.next();
          $ul.append($move);
        }

        // Insert the new <ul> after the previous sibling element
        $ul.insertAfter($li.prev());
      }
    });

    const bodyContent = $('body').html();

    return bodyContent;
  };

  /** 🔥 Shared AI Generation Logic */
  public async startGenerating(): Promise<boolean> {
    const prompt = this.promptFunction?.();
    if (!prompt) return false;

    this.setAiStatus('loading');
    this.aiGeneratedContent = '';
    this.content = '';

    this.aiService.getCompletion(prompt, { stream: true }).subscribe({
      next: (chunk: string) => {
        this.aiGeneratedContent += chunk;
        this.content += chunk;
        this.renderContent(this.content);
      },
      error: (err) => {
        console.error('AI generation failed:', err);
        this.setAiStatus('error');
      },
      complete: () => {
        this.content = this.optimizeHTMLContent(this.content);
        this.renderContent(this.content);
        this.setAiStatus('done');
      },
    });

    return true;
  }

  /** 🔧 Editor-specific content rendering (override this!) */
  protected abstract renderContent(html: string): void;

  // Other abstract methods
  abstract initializeEditor(editorID?: string, content?: string): void;
  abstract clearEditor(): void;
  abstract setHTMLContent(
    value: string,
    muteChangeEmit?: boolean,
  ): Promise<void>;
  abstract saveEditorCurrentValue(): void;
  abstract getEditorID(): string;
}
